import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Play, Calendar, ChevronRight, Trophy } from "lucide-react";
import { API } from "@/config";

export default function CoachEraPage() {
  const { eraId } = useParams();
  const [era, setEra] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchEraData();
  }, [eraId]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch on era change
  
  const fetchEraData = async () => {
    try {
      const [eraRes, seasonsRes] = await Promise.all([
        axios.get(`${API}/coach-eras/${eraId}`),
        axios.get(`${API}/seasons?coach_era_id=${eraId}`)
      ]);
      setEra(eraRes.data);
      setSeasons(seasonsRes.data);
    } catch (err) {
      console.error("Error fetching era:", err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  
  if (!era) {
    return (
      <div className="container" style={{ padding: '4rem 1rem' }}>
        <div className="empty-state">
          <h3 className="empty-state-title">Era Not Found</h3>
          <Link to="/vault" className="btn btn-primary" style={{ marginTop: '1rem' }}>
            Back to The Vault
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container" style={{ padding: '2rem 1rem 4rem' }}>
      <nav className="breadcrumb">
        <Link to="/vault">The Vault</Link>
        <ChevronRight size={16} className="breadcrumb-separator" />
        <span>{era.coach_name}</span>
      </nav>
      
      <div className="era-header">
        <div className="era-header-content">
          <h1 className="era-title">{era.coach_name}</h1>
          <p className="era-years">{era.years}</p>
          {era.championships > 0 && (
            <div className="era-championships">
              <Trophy size={20} />
              <span>{era.championships} National {era.championships === 1 ? 'Championship' : 'Championships'}</span>
            </div>
          )}
          <p className="era-description">{era.description}</p>
        </div>
        {era.thumbnail && (
          <div className="era-header-image">
            <img src={era.thumbnail} alt={era.coach_name} />
          </div>
        )}
      </div>
      
      <div className="section-header" style={{ marginTop: '3rem' }}>
        <h2 className="section-title">Seasons</h2>
        <span className="btn btn-secondary" style={{ cursor: 'default' }}>
          {seasons.length} {seasons.length === 1 ? 'Season' : 'Seasons'}
        </span>
      </div>
      
      {seasons.length === 0 ? (
        <div className="empty-state">
          <Calendar className="empty-state-icon" />
          <h3 className="empty-state-title">No Seasons Yet</h3>
          <p className="empty-state-text">Seasons will be added soon.</p>
        </div>
      ) : (
        <div className="season-list">
          {seasons.map((season) => (
            <Link 
              key={season.id} 
              to={`/season/${season.id}`} 
              className="season-list-item"
              data-testid={`season-${season.year}`}
            >
              <div className="season-list-info">
                <h3 className="season-list-year">{season.year}</h3>
                <p className="season-list-title">{season.title}</p>
              </div>
              <div className="season-list-meta">
                <span className="season-list-count">
                  <Play size={16} />
                  {season.video_count} {season.video_count === 1 ? 'Game' : 'Games'}
                </span>
                <ChevronRight size={20} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
